import { API_BASE_URL } from '../config.js';

/**
 * Formats text responses data for AI analysis
 * @param {Array} responses - Array of response objects with value, promoter, date
 * @returns {Array} Array of response values formatted for analysis
 */
export const formatResponsesForAnalysis = (responses) => {
  return responses
    .filter(response => response.value && typeof response.value === 'string' && response.value.trim() !== '')
    .map(response => response.value.trim());
};

/**
 * Generates AI analysis for text responses
 * @param {Object} params - Analysis parameters
 * @param {Array} params.responses - Array of response objects
 * @param {Function} params.fetchWithAuth - Authenticated fetch function
 * @param {number} params.campaignId - Campaign ID for saving analysis (optional if noPersistance=true)
 * @param {number} params.questionId - Question ID for saving analysis (optional if noPersistance=true)
 * @param {boolean} params.force - Force regeneration if analysis exists (optional)
 * @param {boolean} params.noPersistance - If true, don't save to DB, only use memory cache (optional)
 * @returns {Promise<Object>} Analysis result
 */
export const generateAIAnalysis = async ({
  responses,
  fetchWithAuth,
  campaignId = null,
  questionId = null,
  force = false,
  noPersistance = false
}) => {
  const formattedData = formatResponsesForAnalysis(responses);

  if (formattedData.length === 0) {
    throw new Error("Aucune réponse valide à analyser");
  }

  const requestBody = {
    data: formattedData
  };

  // Add force parameter if needed
  if (force) {
    requestBody.force = true;
  }

  // Add campaign and question IDs for database saving if provided and not in noPersistance mode
  if (!noPersistance && campaignId && questionId) {
    requestBody.campaignId = parseInt(campaignId);
    requestBody.questionId = parseInt(questionId);
  }

  // Build URL with query parameter if needed
  const url = noPersistance
    ? `${API_BASE_URL}/api/ia/generate-analysis?noPersistance=true`
    : `${API_BASE_URL}/api/ia/generate-analysis`;

  const response = await fetchWithAuth(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    throw new Error(`Erreur ${response.status}: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Retrieves existing AI analysis for a specific campaign and question
 * @param {Object} params - Retrieval parameters
 * @param {Function} params.fetchWithAuth - Authenticated fetch function
 * @param {number} params.campaignId - Campaign ID
 * @param {number} params.questionId - Question ID
 * @returns {Promise<Object|null>} Existing analysis or null if not found
 */
export const retrieveExistingAnalysis = async ({ fetchWithAuth, campaignId, questionId }) => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/ia/retrieve-analysis?campaignId=${campaignId}&questionId=${questionId}`
    );

    if (response.ok) {
      return await response.json();
    } else if (response.status === 404) {
      // No existing analysis found
      return null;
    } else {
      console.warn(`Erreur lors de la récupération de l'analyse: ${response.status}`);
      return null;
    }
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'analyse existante:', error);
    return null;
  }
};

/**
 * Formats analysis timestamp for display
 * @param {string} timestamp - ISO timestamp string
 * @returns {string|null} Formatted date string or null
 */
export const formatAnalysisDate = (timestamp) => {
  if (!timestamp) return null;

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    // Relative format for recent dates
    if (diffInHours < 1) {
      return "Il y a moins d'1 heure";
    } else if (diffInHours < 24) {
      return `Il y a ${diffInHours} heure${diffInHours > 1 ? 's' : ''}`;
    } else if (diffInDays < 7) {
      return `Il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}`;
    } else {
      // Absolute format for older dates
      return `Le ${date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    }
  } catch (error) {
    console.warn('Erreur lors du formatage de la date d\'analyse:', error);
    return null;
  }
};